import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCheck, UserX, Trash2, Clock, CheckCircle, XCircle, Building, MapPin, Briefcase, Gift, X, CreditCard, FileText, BarChart3, Sparkles, Globe, Ticket, ArrowUpCircle, Settings, Image as ImageIcon, Code2, Loader2, KeyRound, LogIn } from 'lucide-react';
import KineticHero from '@/components/KineticHero';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminConfigPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [giftModal, setGiftModal] = useState(null);
  const [giftCount, setGiftCount] = useState(1);
  const [upgradeModal, setUpgradeModal] = useState(null);
  const [upgradePkg, setUpgradePkg] = useState('strategist');
  const [upgradeReports, setUpgradeReports] = useState(1);
  const [upgradeNote, setUpgradeNote] = useState('');
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [giftRequests, setGiftRequests] = useState([]);
  const [giftReqLoading, setGiftReqLoading] = useState(false);
  const [approveCount, setApproveCount] = useState(1);
  const [redemptions, setRedemptions] = useState([]);
  const [ccEmail, setCcEmail] = useState('');
  const [savedCcEmail, setSavedCcEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  // Email diagnostics state
  const [emailDiag, setEmailDiag] = useState(null);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testSenderType, setTestSenderType] = useState('platform');
  const [testingEmail, setTestingEmail] = useState(false);

  // Illustration provider toggle (Gemini Nano Banana ↔ Claude Sonnet 4.6 HTML→PNG)
  const [illustProvider, setIllustProvider] = useState('gemini');
  const [illustOptions, setIllustOptions] = useState([]);
  const [illustSaving, setIllustSaving] = useState(false);

  const fetchUsers = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUsers(res.data))
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); fetchGiftRequests(); fetchRedemptions(); fetchEmailConfig(); fetchEmailDiag(); fetchIllustProvider(); }, []);

  const fetchIllustProvider = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/config/illustration-provider`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setIllustProvider(res.data?.value || 'gemini'); setIllustOptions(res.data?.options || []); })
      .catch(() => {});
  };

  const toggleIllustProvider = async (nextValue) => {
    if (nextValue === illustProvider || illustSaving) return;
    setIllustSaving(true);
    const token = localStorage.getItem('token');
    try {
      const r = await axios.put(`${API}/admin/config/illustration-provider`, { value: nextValue },
        { headers: { Authorization: `Bearer ${token}` } });
      setIllustProvider(r.data.value);
      toast.success(`Illustrations will now be generated via ${nextValue === 'claude_html' ? 'Claude Sonnet 4.6' : 'Gemini Nano Banana'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to switch provider');
    } finally { setIllustSaving(false); }
  };

  const fetchEmailDiag = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/email-diagnostics`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setEmailDiag(res.data); if (!testEmailTo) setTestEmailTo(res.data.admin_email_bcc || ''); })
      .catch(() => {});
  };

  const sendDiagTest = async () => {
    if (!testEmailTo.trim() || !testEmailTo.includes('@')) return toast.error('Enter a valid recipient email');
    const token = localStorage.getItem('token');
    setTestingEmail(true);
    try {
      const r = await axios.post(`${API}/admin/email-diagnostics/test`, { to: testEmailTo, sender_type: testSenderType }, { headers: { Authorization: `Bearer ${token}` } });
      if (r.data.success) toast.success(`Test email dispatched (id: ${r.data.detail?.id || 'n/a'})`);
      else toast.error(`Send failed: ${r.data.detail?.reason || 'unknown'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
    finally { setTestingEmail(false); }
  };

  const fetchEmailConfig = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/email-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setCcEmail(res.data.cc_email || ''); setSavedCcEmail(res.data.cc_email || ''); })
      .catch(() => {});
  };

  const saveEmailConfig = async () => {
    if (!ccEmail.trim() || !ccEmail.includes('@')) return toast.error('Enter a valid email');
    const token = localStorage.getItem('token');
    setSavingEmail(true);
    try {
      await axios.put(`${API}/admin/email-config`, { cc_email: ccEmail }, { headers: { Authorization: `Bearer ${token}` } });
      setSavedCcEmail(ccEmail);
      toast.success('CC email updated — all platform emails will be copied here');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingEmail(false); }
  };

  const fetchRedemptions = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/admin/coupon-redemptions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setRedemptions(res.data))
      .catch(() => {});
  };

  const fetchGiftRequests = () => {
    const token = localStorage.getItem('token');
    setGiftReqLoading(true);
    axios.get(`${API}/admin/free-access-requests`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setGiftRequests(res.data))
      .catch(() => {})
      .finally(() => setGiftReqLoading(false));
  };

  const handleGiftReqAction = async (reqId, action, count) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.put(`${API}/admin/free-access-requests/${reqId}`, { action, gift_count: count }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchGiftRequests();
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const approveUser = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User approved — activation email sent');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
  };

  const rejectUser = async (userId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User rejected');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const resetUserPassword = async (user) => {
    if (!window.confirm(
      `Send a password-reset email to ${user.email}?\n\nThe user will receive a one-time link valid for 1 hour.`
    )) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/admin/users/${user.id}/reset-password`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reset email sent to ${user.email} (link valid 1h)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset email');
    }
  };

  const impersonateUser = async (user) => {
    if (!window.confirm(
      `Sign in as ${user.email}?\n\n` +
      `You'll see Sovereign IQ exactly as this user does. The session is logged for audit. ` +
      `To return to your admin account, sign out.`
    )) return;
    const token = localStorage.getItem('token');
    try {
      const { data } = await axios.post(`${API}/admin/users/${user.id}/impersonate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      // Save the original admin token so we can restore on exit.
      localStorage.setItem('admin_token_backup', token);
      localStorage.setItem('impersonator_email', JSON.parse(atob(token.split('.')[1])).email);
      localStorage.setItem('token', data.token);
      toast.success(`Now signed in as ${user.email}`);
      // Hard reload so the entire app re-fetches as the impersonated user.
      setTimeout(() => { window.location.href = '/platform'; }, 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not impersonate');
    }
  };

  const grantFreeReports = async () => {
    if (!giftModal || giftCount < 1) return;
    const token = localStorage.getItem('token');
    try {
      const res = await axios.put(`${API}/admin/users/${giftModal.id}/free-reports`, { count: giftCount }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setGiftModal(null);
      setGiftCount(1);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitUpgrade = async () => {
    if (!upgradeModal) return;
    const token = localStorage.getItem('token');
    try {
      setUpgradeSubmitting(true);
      const res = await axios.put(`${API}/admin/users/${upgradeModal.id}/upgrade-package`,
        { package: upgradePkg, reports: upgradeReports, note: upgradeNote },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setUpgradeModal(null);
      setUpgradePkg('strategist');
      setUpgradeReports(1);
      setUpgradeNote('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upgrade failed');
    } finally { setUpgradeSubmitting(false); }
  };

  const filtered = users.filter(u => filter === 'all' || u.status === filter);
  const pendingCount = users.filter(u => u.status === 'pending').length;
  const activeCount = users.filter(u => u.status === 'active').length;
  const rejectedCount = users.filter(u => u.status === 'rejected').length;

  const statusBadge = (s) => {
    if (s === 'active') return <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full"><CheckCircle className="w-3 h-3" /> Active</span>;
    if (s === 'pending') return <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" /> Pending</span>;
    return <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><XCircle className="w-3 h-3" /> Rejected</span>;
  };

  const pendingReqs = giftRequests.filter(r => r.status === 'pending').length;

  return (
    <div data-testid="admin-config-page" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="admcfg"
        palette="violet"
        eyebrow="Admin · approvals, gifts, coupons"
        title="User"
        accent="Management"
        subtitle="Approve registrations, gift complimentary reports, and review coupon redemptions — all in one console."
        right={
          <div className="flex bg-white rounded-lg shadow-sm p-1 gap-1">
            <button onClick={() => setActiveTab('users')} data-testid="tab-users"
              className={`px-4 py-2 rounded-md text-xs font-semibold transition-all ${activeTab === 'users' ? 'bg-[#004ac6] text-white' : 'text-[#434655] hover:bg-[#f8f9ff]'}`}>
              Users
            </button>
            <button onClick={() => setActiveTab('gift-requests')} data-testid="tab-gift-requests"
              className={`px-4 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'gift-requests' ? 'bg-amber-500 text-white' : 'text-[#434655] hover:bg-[#f8f9ff]'}`}>
              <Sparkles className="w-3.5 h-3.5" /> Gift Requests
              {pendingReqs > 0 && <span className="bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{pendingReqs}</span>}
            </button>
            <button onClick={() => setActiveTab('coupons-redeemed')} data-testid="tab-coupons-redeemed"
              className={`px-4 py-2 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'coupons-redeemed' ? 'bg-[#7c3aed] text-white' : 'text-[#434655] hover:bg-[#f8f9ff]'}`}>
              <Ticket className="w-3.5 h-3.5" /> Coupons Redeemed
              {redemptions.length > 0 && <span className="text-[9px] font-bold bg-[#7c3aed]/20 text-[#7c3aed] px-1.5 py-0.5 rounded-full">{redemptions.length}</span>}
            </button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">

      {/* ── SYSTEM SETTINGS · Global illustration provider toggle ── */}
      <div className="bg-white rounded-xl shadow-sm border border-[#c3c6d7]/30 overflow-hidden" data-testid="system-settings-panel">
        <div className="px-5 py-3 border-b border-[#c3c6d7]/20 bg-gradient-to-r from-[#f8f9ff] to-white flex items-center gap-2">
          <Settings className="w-4 h-4 text-[#7c3aed]" />
          <span className="text-xs font-bold uppercase tracking-widest text-[#434655]">System settings</span>
          <span className="text-[10px] text-[#737686] ml-2">Applies to every illustration across the entire application</span>
        </div>
        <div className="p-5 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 shrink-0">
            <ImageIcon className="w-4 h-4 text-[#059669]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#434655]">Illustration engine</span>
          </div>
          <div className="flex items-center rounded-full bg-[#f8f9ff] border border-[#c3c6d7]/50 p-0.5 shadow-inner"
               role="radiogroup"
               aria-label="Illustration provider selector">
            <button
              onClick={() => toggleIllustProvider('gemini')}
              disabled={illustSaving}
              role="radio"
              aria-checked={illustProvider === 'gemini'}
              data-testid="illust-provider-gemini"
              title="Gemini Nano Banana — direct AI image generation"
              className={`relative px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-all flex items-center gap-1.5
                ${illustProvider === 'gemini'
                  ? 'bg-gradient-to-r from-[#f59e0b] to-[#ea580c] text-white shadow-sm'
                  : 'text-[#737686] hover:text-[#434655] hover:bg-white'}
                ${illustSaving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <Sparkles className="w-3 h-3" />
              Gemini Nano Banana
            </button>
            <button
              onClick={() => toggleIllustProvider('claude_html')}
              disabled={illustSaving}
              role="radio"
              aria-checked={illustProvider === 'claude_html'}
              data-testid="illust-provider-claude"
              title="Claude Sonnet 4.6 — HTML infographic rendered via headless Chromium"
              className={`relative px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-all flex items-center gap-1.5
                ${illustProvider === 'claude_html'
                  ? 'bg-gradient-to-r from-[#7c3aed] to-[#db2777] text-white shadow-sm'
                  : 'text-[#737686] hover:text-[#434655] hover:bg-white'}
                ${illustSaving ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <Code2 className="w-3 h-3" />
              Claude Sonnet 4.6 · HTML
            </button>
          </div>
          {illustSaving && <Loader2 className="w-3.5 h-3.5 text-[#7c3aed] animate-spin" />}
          <span className="ml-auto text-[10px] text-[#737686] max-w-[420px] text-right">
            {illustProvider === 'claude_html'
              ? 'Claude generates a self-contained HTML infographic (A4-safe 900px), rendered to PNG via headless Chromium. Resilient to Gemini outages. Uses Claude text credits.'
              : 'Direct AI image generation via Gemini Nano Banana. Fast, photo-like — depends on upstream Gemini deployment availability.'}
          </span>
        </div>
      </div>

      {activeTab === 'users' && (<>

      {/* Email Diagnostics — live deliverability check */}
      <div className="bg-white rounded-xl shadow-sm p-4" data-testid="email-diagnostics-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center shrink-0">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0b1c30]">Email Diagnostics</p>
              <p className="text-[10px] text-[#737686]">Live deliverability check — Resend pipeline</p>
            </div>
          </div>
          <button onClick={fetchEmailDiag} className="text-[10px] text-[#004ac6] hover:underline" data-testid="refresh-email-diag">Refresh</button>
        </div>

        {emailDiag ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg border border-[#c3c6d7]/30 p-3 bg-[#f8f9ff]">
              <p className="text-[9px] uppercase tracking-widest text-[#737686]">API Key</p>
              <p className={`text-xs font-bold ${emailDiag.resend_api_configured ? 'text-emerald-600' : 'text-red-600'}`}>
                {emailDiag.resend_api_configured ? `Configured (${emailDiag.resend_api_key_preview})` : 'Missing'}
              </p>
            </div>
            <div className="rounded-lg border border-[#c3c6d7]/30 p-3 bg-[#f8f9ff]">
              <p className="text-[9px] uppercase tracking-widest text-[#737686]">Verified Domain</p>
              <p className={`text-xs font-bold ${emailDiag.verified_domain?.verified ? 'text-emerald-600' : 'text-red-600'}`}>
                {emailDiag.verified_domain?.domain || '—'} {emailDiag.verified_domain?.verified ? '✓' : ''}
              </p>
              {emailDiag.verified_domain?.region && <p className="text-[9px] text-[#737686] mt-0.5">{emailDiag.verified_domain.region}</p>}
            </div>
            <div className="rounded-lg border border-[#c3c6d7]/30 p-3 bg-[#f8f9ff]">
              <p className="text-[9px] uppercase tracking-widest text-[#737686]">Platform Sender</p>
              <p className="text-xs font-bold text-[#0b1c30] truncate">{emailDiag.sender_email || '—'}</p>
              <p className="text-[9px] text-[#737686] mt-0.5 truncate">Gift: {emailDiag.sender_email_gift || '—'}</p>
            </div>
            <div className="rounded-lg border border-[#c3c6d7]/30 p-3 bg-[#f8f9ff]">
              <p className="text-[9px] uppercase tracking-widest text-[#737686]">Admin BCC</p>
              <p className="text-xs font-bold text-[#0b1c30] truncate">{emailDiag.admin_email_bcc || '—'}</p>
            </div>
          </div>
        ) : <p className="text-[11px] text-[#737686] mb-3">Loading diagnostics…</p>}

        {/* Test send */}
        <div className="flex items-center gap-2 flex-wrap border-t border-[#c3c6d7]/20 pt-3">
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#737686]">Send Test</span>
          <select value={testSenderType} onChange={e => setTestSenderType(e.target.value)}
            data-testid="email-test-sender-type"
            className="bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-2 py-1.5 text-xs text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none">
            <option value="platform">Platform (platform@)</option>
            <option value="gift">Gift (gift@)</option>
            <option value="coupon">Coupon (coupon@)</option>
          </select>
          <input type="email" value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)}
            placeholder="recipient@example.com"
            className="flex-1 min-w-[220px] bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-3 py-1.5 text-xs text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
            data-testid="email-test-to" />
          <button onClick={sendDiagTest} disabled={testingEmail}
            data-testid="email-test-send-btn"
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${testingEmail ? 'bg-[#c3c6d7]/20 text-[#737686] cursor-not-allowed' : 'bg-[#004ac6] text-white hover:bg-[#003ea8]'}`}>
            {testingEmail ? 'Sending…' : 'Send Test'}
          </button>
        </div>
        <p className="text-[10px] text-[#737686] mt-2">Tip: after a test send, check the recipient inbox, and verify the Sovereign IQ BCC address received a copy too.</p>
      </div>

      {/* Email CC Config */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between" data-testid="email-cc-config">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-8 h-8 rounded-lg bg-[#e5eeff] flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-[#004ac6]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#0b1c30]">Email Copy (BCC)</p>
            <p className="text-[10px] text-[#737686]">All platform emails will be copied to this address</p>
          </div>
          <input type="email" value={ccEmail} onChange={e => setCcEmail(e.target.value)}
            placeholder="admin@example.com"
            className="w-72 bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none"
            data-testid="cc-email-input" />
          <button onClick={saveEmailConfig} disabled={savingEmail || ccEmail === savedCcEmail}
            data-testid="save-cc-email"
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${ccEmail !== savedCcEmail ? 'bg-[#004ac6] text-white hover:bg-[#003ea8]' : 'bg-[#c3c6d7]/20 text-[#737686] cursor-not-allowed'}`}>
            {savingEmail ? 'Saving...' : ccEmail !== savedCcEmail ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-all" onClick={() => setFilter('all')} data-testid="filter-all">
          <p className="text-2xl font-extrabold text-[#0b1c30]">{users.length}</p>
          <p className="text-[10px] text-[#434655] uppercase tracking-widest">Total Users</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-all border-l-4 border-amber-400" onClick={() => setFilter('pending')} data-testid="filter-pending">
          <p className="text-2xl font-extrabold text-amber-600">{pendingCount}</p>
          <p className="text-[10px] text-[#434655] uppercase tracking-widest">Pending Approval</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-all border-l-4 border-green-400" onClick={() => setFilter('active')} data-testid="filter-active">
          <p className="text-2xl font-extrabold text-green-600">{activeCount}</p>
          <p className="text-[10px] text-[#434655] uppercase tracking-widest">Active</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-all border-l-4 border-red-400" onClick={() => setFilter('rejected')} data-testid="filter-rejected">
          <p className="text-2xl font-extrabold text-red-600">{rejectedCount}</p>
          <p className="text-[10px] text-[#434655] uppercase tracking-widest">Rejected</p>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-[#0b1c30] text-white flex justify-between items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest">Registered Users</span>
            <span className="text-[10px] text-white/50 ml-3">{filtered.length} shown</span>
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'active', 'rejected'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`text-[10px] font-semibold px-2 py-0.5 rounded transition-all ${filter === f ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'}`}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[#434655] text-xs">Loading users...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[#434655] text-xs italic">No users found</div>
        ) : (
          <div className="divide-y divide-[#c3c6d7]/20">
            {filtered.map(user => {
              const s = user._stats || {};
              return (
              <div key={user.id} className="px-5 py-4 hover:bg-[#f8f9ff] transition-all" data-testid={`user-row-${user.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-[#0b1c30]">{user.full_name || 'Unknown'}</span>
                      {statusBadge(user.status || 'pending')}
                      {user.role === 'Admin' && <span className="text-[9px] font-bold text-[#004ac6] bg-[#e5eeff] px-1.5 py-0.5 rounded">ADMIN</span>}
                      {user.subscription_tier && <span className="text-[9px] font-bold text-[#7c3aed] bg-[#f3f0ff] px-1.5 py-0.5 rounded">{user.subscription_tier}</span>}
                    </div>
                    <p className="text-xs text-[#434655]">{user.email}</p>
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      {user.company && <span className="flex items-center gap-1 text-[10px] text-[#434655]"><Building className="w-3 h-3" /> {user.company}</span>}
                      {user.location && <span className="flex items-center gap-1 text-[10px] text-[#434655]"><MapPin className="w-3 h-3" /> {user.location}</span>}
                      <span className="text-[10px] text-[#c3c6d7]">Registered: {new Date(user.created_at).toLocaleDateString()}</span>
                    </div>
                    {/* Report Stats Row */}
                    {user.role !== 'Admin' && user.status === 'active' && (
                      <div className="flex flex-wrap items-center gap-3 mt-2 bg-[#f8f9ff] rounded-lg px-3 py-2">
                        <span className="flex items-center gap-1 text-[10px] text-[#434655]">
                          <FileText className="w-3 h-3 text-[#004ac6]" /> Used: <strong className="text-[#0b1c30]">{Math.max(0, (s.reports_used || 0) - 1)}</strong>
                        </span>
                        <span className="text-[#c3c6d7]">|</span>
                        {(s.paid_reports > 0) && (
                          <>
                            <span className="flex items-center gap-1 text-[10px] text-[#434655]">
                              <CreditCard className="w-3 h-3 text-[#00A651]" /> Paid: <strong className="text-[#00A651]">{s.paid_reports}</strong>
                            </span>
                            <span className="text-[#c3c6d7]">|</span>
                          </>
                        )}
                        {(s.free_from_admin > 0) && (
                          <>
                            <span className="flex items-center gap-1 text-[10px] text-[#434655]">
                              <Gift className="w-3 h-3 text-[#004ac6]" /> Gift: <strong className="text-[#004ac6]">{s.free_from_admin}</strong>
                            </span>
                            <span className="text-[#c3c6d7]">|</span>
                          </>
                        )}
                        <span className="text-[#c3c6d7]">|</span>
                        <span className="flex items-center gap-1 text-[10px]">
                          Remaining: <strong className={s.reports_remaining > 0 ? 'text-[#00A651]' : 'text-red-500'}>{s.reports_remaining ?? 0}</strong>
                        </span>
                        {s.total_paid_amount > 0 && (
                          <>
                            <span className="text-[#c3c6d7]">|</span>
                            <span className="flex items-center gap-1 text-[10px] font-semibold text-[#00A651]">
                              <BarChart3 className="w-3 h-3" /> Revenue: ${s.total_paid_amount}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    {user.areas_of_interest?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {user.areas_of_interest.map((a, i) => (
                          <span key={i} className="text-[9px] font-medium bg-[#f8f9ff] text-[#434655] px-1.5 py-0.5 rounded border border-[#c3c6d7]/20 flex items-center gap-0.5">
                            <Briefcase className="w-2.5 h-2.5" /> {a.length > 30 ? a.slice(0, 30) + '...' : a}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 ml-4 shrink-0">
                    {user.status === 'active' && user.role !== 'Admin' && (
                      <button onClick={() => { setUpgradeModal(user); setUpgradePkg('strategist'); setUpgradeReports(1); setUpgradeNote(''); }} data-testid={`upgrade-${user.id}`}
                        className="p-2 rounded-lg bg-[#f3f0ff] text-[#7c3aed] hover:bg-[#e9e3ff] transition-all" title="Complementary Upgrade">
                        <ArrowUpCircle className="w-4 h-4" />
                      </button>
                    )}
                    {user.status === 'active' && user.role !== 'Admin' && (
                      <button onClick={() => { setGiftModal(user); setGiftCount(1); }} data-testid={`gift-${user.id}`}
                        className="p-2 rounded-lg bg-[#e5eeff] text-[#004ac6] hover:bg-[#d0dfff] transition-all" title="Gift Reports">
                        <Gift className="w-4 h-4" />
                      </button>
                    )}
                    {(user.status === 'pending' || user.status === 'rejected') && (
                      <button onClick={() => approveUser(user.id)} data-testid={`approve-${user.id}`}
                        className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-all" title="Approve">
                        <UserCheck className="w-4 h-4" />
                      </button>
                    )}
                    {(user.status === 'pending' || user.status === 'active') && user.role !== 'Admin' && (
                      <button onClick={() => rejectUser(user.id)} data-testid={`reject-${user.id}`}
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all" title="Reject">
                        <UserX className="w-4 h-4" />
                      </button>
                    )}
                    {user.role !== 'Admin' && user.status === 'active' && (
                      <button onClick={() => impersonateUser(user)} data-testid={`impersonate-${user.id}`}
                        className="p-2 rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 transition-all" title="Sign in as user">
                        <LogIn className="w-4 h-4" />
                      </button>
                    )}
                    {user.role !== 'Admin' && (
                      <button onClick={() => resetUserPassword(user)} data-testid={`reset-pw-${user.id}`}
                        className="p-2 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all" title="Send password-reset email">
                        <KeyRound className="w-4 h-4" />
                      </button>
                    )}
                    {user.role !== 'Admin' && (
                      <button onClick={() => deleteUser(user.id)} data-testid={`delete-${user.id}`}
                        className="p-2 rounded-lg bg-[#f8f9ff] text-[#434655] hover:bg-red-50 hover:text-red-600 transition-all" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>

      {/* Gift Reports Modal */}
      {giftModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setGiftModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="gift-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-extrabold text-[#0b1c30] flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#004ac6]" /> Gift Reports
              </h3>
              <button onClick={() => setGiftModal(null)} className="p-1 rounded-lg hover:bg-[#f8f9ff] text-[#434655]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#434655] mb-1">Granting gift reports to:</p>
            <p className="text-sm font-semibold text-[#0b1c30] mb-1">{giftModal.full_name}</p>
            <p className="text-xs text-[#737686] mb-4">{giftModal.email}</p>
            {giftModal.free_reports_granted > 0 && (
              <p className="text-[10px] text-[#004ac6] bg-[#e5eeff] rounded-lg px-3 py-1.5 mb-4">
                Already granted: <strong>{giftModal.free_reports_granted}</strong> gift reports
              </p>
            )}
            <label className="text-xs font-semibold text-[#0b1c30] block mb-1.5">Number of additional gift reports</label>
            <input type="number" min="1" max="100" value={giftCount}
              onChange={e => setGiftCount(Math.max(1, +e.target.value))}
              className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-3 py-2.5 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#004ac6] outline-none mb-4"
              data-testid="gift-count-input" />
            <button onClick={grantFreeReports} data-testid="gift-confirm-btn"
              className="w-full bg-[#004ac6] text-white py-2.5 rounded-lg text-sm font-bold hover:bg-[#003ea8] transition-all flex items-center justify-center gap-2">
              <Gift className="w-4 h-4" /> Grant {giftCount} Gift Report{giftCount > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Complementary Upgrade Modal */}
      {upgradeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setUpgradeModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="upgrade-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-extrabold text-[#0b1c30] flex items-center gap-2">
                <ArrowUpCircle className="w-5 h-5 text-[#7c3aed]" /> Complementary Upgrade
              </h3>
              <button onClick={() => setUpgradeModal(null)} className="p-1 rounded-lg hover:bg-[#f8f9ff] text-[#434655]"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#434655] mb-1">Upgrading package for:</p>
            <p className="text-sm font-semibold text-[#0b1c30] mb-0.5">{upgradeModal.full_name}</p>
            <p className="text-xs text-[#737686] mb-1">{upgradeModal.email}</p>
            {upgradeModal.subscription_tier && (
              <p className="text-[10px] text-[#7c3aed] bg-[#f3f0ff] rounded-lg px-2.5 py-1 mb-4 inline-block">
                Current tier: <strong>{upgradeModal.subscription_tier}</strong>
              </p>
            )}

            <label className="text-xs font-semibold text-[#0b1c30] block mb-2 mt-3">Target package</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { id: 'scout', label: 'Scout', color: '#004ac6', hint: '12 sections' },
                { id: 'strategist', label: 'Strategist', color: '#00A651', hint: '22 sections' },
                { id: 'sovereign', label: 'Sovereign', color: '#7c3aed', hint: '31 sections' },
              ].map(opt => (
                <button key={opt.id} onClick={() => setUpgradePkg(opt.id)}
                  data-testid={`upgrade-pkg-${opt.id}`}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${upgradePkg === opt.id ? 'border-current shadow-md' : 'border-[#c3c6d7]/30 hover:border-[#c3c6d7]/60'}`}
                  style={upgradePkg === opt.id ? { color: opt.color, backgroundColor: opt.color + '15' } : {}}>
                  <div className="text-[11px] font-bold uppercase tracking-wider">{opt.label}</div>
                  <div className="text-[9px] text-[#737686] mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>

            <label className="text-xs font-semibold text-[#0b1c30] block mb-1.5">Report credits to grant</label>
            <input type="number" min="0" max="100" value={upgradeReports}
              onChange={e => setUpgradeReports(Math.max(0, +e.target.value))}
              className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-[#7c3aed] outline-none mb-3"
              data-testid="upgrade-reports-input" />

            <label className="text-xs font-semibold text-[#0b1c30] block mb-1.5">Note (included in email, optional)</label>
            <textarea rows="2" value={upgradeNote} onChange={e => setUpgradeNote(e.target.value)}
              placeholder="e.g. Thanks for the partnership — enjoy your Strategist access."
              className="w-full bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-xs text-[#0b1c30] focus:ring-1 focus:ring-[#7c3aed] outline-none mb-4 resize-none"
              data-testid="upgrade-note-input" />

            <button onClick={submitUpgrade} disabled={upgradeSubmitting} data-testid="upgrade-confirm-btn"
              className="w-full bg-[#7c3aed] text-white py-2.5 rounded-lg text-sm font-bold hover:bg-[#6d28d9] transition-all flex items-center justify-center gap-2 disabled:opacity-60">
              {upgradeSubmitting ? <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Upgrading…</> :
                <><ArrowUpCircle className="w-4 h-4" /> Grant {upgradePkg.charAt(0).toUpperCase() + upgradePkg.slice(1)} Upgrade</>}
            </button>
            <p className="text-[9px] text-[#737686] text-center mt-2">User will receive a notification email.</p>
          </div>
        </div>
      )}

      </>)}

      {/* Gift Requests Tab */}
      {activeTab === 'gift-requests' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-amber-400">
              <p className="text-2xl font-extrabold text-amber-600">{giftRequests.filter(r => r.status === 'pending').length}</p>
              <p className="text-[10px] text-[#434655] uppercase tracking-widest">Pending Requests</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-400">
              <p className="text-2xl font-extrabold text-green-600">{giftRequests.filter(r => r.status === 'approved').length}</p>
              <p className="text-[10px] text-[#434655] uppercase tracking-widest">Approved</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-400">
              <p className="text-2xl font-extrabold text-red-600">{giftRequests.filter(r => r.status === 'rejected').length}</p>
              <p className="text-[10px] text-[#434655] uppercase tracking-widest">Rejected</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-[#0b1c30] text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest">Gift Report Requests</span>
              <span className="text-[10px] text-white/50 ml-2">{giftRequests.length} total</span>
            </div>
            {giftReqLoading ? (
              <div className="p-8 text-center text-[#434655] text-xs">Loading...</div>
            ) : giftRequests.length === 0 ? (
              <div className="p-8 text-center text-[#434655] text-xs italic">No gift requests yet</div>
            ) : (
              <div className="divide-y divide-[#c3c6d7]/20">
                {giftRequests.map(req => (
                  <div key={req.id} className="px-5 py-4 hover:bg-[#f8f9ff] transition-all" data-testid={`gift-req-${req.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-[#0b1c30]">{req.user_name}</span>
                          {req.status === 'pending' && <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> Pending</span>}
                          {req.status === 'approved' && <span className="text-[9px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><CheckCircle className="w-2.5 h-2.5" /> Approved ({req.gift_count})</span>}
                          {req.status === 'rejected' && <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><XCircle className="w-2.5 h-2.5" /> Rejected</span>}
                        </div>
                        <p className="text-xs text-[#434655]">{req.user_email}</p>
                        <div className="flex flex-wrap gap-3 mt-1.5">
                          <span className="flex items-center gap-1 text-[10px] text-[#434655]"><Globe className="w-3 h-3" /> {req.country}</span>
                          <span className="flex items-center gap-1 text-[10px] text-[#004ac6] font-semibold"><Briefcase className="w-3 h-3" /> {req.sector}</span>
                          <span className="text-[10px] text-[#c3c6d7]">{new Date(req.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        {req.message && <p className="text-xs text-[#737686] mt-1.5 italic">"{req.message}"</p>}
                      </div>
                      {req.status === 'pending' && (
                        <div className="flex items-center gap-2 ml-4 shrink-0">
                          <input type="number" min="1" max="50" defaultValue={1}
                            onChange={e => setApproveCount(Math.max(1, +e.target.value))}
                            className="w-14 bg-[#f8f9ff] border border-[#c3c6d7]/40 rounded-lg px-2 py-1.5 text-xs text-center text-[#0b1c30] focus:ring-1 focus:ring-green-400 outline-none"
                            data-testid={`req-count-${req.id}`} />
                          <button onClick={() => handleGiftReqAction(req.id, 'approve', approveCount)}
                            data-testid={`req-approve-${req.id}`}
                            className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-all" title="Approve & Grant">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleGiftReqAction(req.id, 'reject', 0)}
                            data-testid={`req-reject-${req.id}`}
                            className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-all" title="Reject">
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Coupons Redeemed Tab */}
      {activeTab === 'coupons-redeemed' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-[#0b1c30] text-white flex items-center gap-2">
            <Ticket className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Coupon Redemptions</span>
            <span className="text-[10px] text-white/50 ml-2">{redemptions.length} total</span>
          </div>
          {redemptions.length === 0 ? (
            <div className="p-8 text-center text-[#434655] text-xs italic">No coupons have been redeemed yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#f8f9ff] text-[10px] uppercase tracking-widest text-[#434655]">
                  <th className="text-left px-5 py-3 font-semibold">User</th>
                  <th className="text-left px-5 py-3 font-semibold">Email</th>
                  <th className="text-left px-5 py-3 font-semibold">Coupon Code</th>
                  <th className="text-center px-5 py-3 font-semibold">Reports Granted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#c3c6d7]/20">
                {redemptions.map((r, i) => (
                  <tr key={i} className="hover:bg-[#f8f9ff]">
                    <td className="px-5 py-3 font-medium text-[#0b1c30]">{r.user_name}</td>
                    <td className="px-5 py-3 text-[#434655]">{r.user_email}</td>
                    <td className="px-5 py-3"><span className="font-mono tracking-wider text-[#7c3aed] font-bold bg-[#f3f0ff] px-2 py-0.5 rounded">{r.coupon_code}</span></td>
                    <td className="px-5 py-3 text-center font-bold text-[#0b1c30]">{r.reports}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
